// Admin Customers list. Registered users + guests-with-history, with order
// count and lifetime value. Free-text search on name + email. Each row links
// to the full profile at /admin/customers/:id (or :email for guests).
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../../lib/api'

interface CustomerRow {
  kind: 'registered' | 'guest'
  id: string | null
  email: string
  firstName: string | null
  lastName: string | null
  fullName: string | null
  role: string
  createdAt: string
  lastLoginAt: string | null
  orderCount: number
  ltvCents: number
  lastOrderAt: string | null
}

export default function AdminCustomers() {
  const { getAccessToken } = useAuth()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [data, setData] = useState<CustomerRow[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true)
      try {
        const qs = search ? `?search=${encodeURIComponent(search)}` : ''
        const d = await apiFetch<{ customers: CustomerRow[] }>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/customers${qs}`)
        setData(d.customers); setErr(null)
      } catch (e) { setErr((e as Error).message) }
      finally { setLoading(false) }
    }, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  return (
    <div className="p-8">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="eyebrow">Customers</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">Customers</h1>
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email…" className="input max-w-xs" />
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
            <tr>
              <th className="text-left px-4 py-2 font-bold">Name</th>
              <th className="text-left px-4 py-2 font-bold">Email</th>
              <th className="text-left px-4 py-2 font-bold">Role</th>
              <th className="text-right px-4 py-2 font-bold">Orders</th>
              <th className="text-right px-4 py-2 font-bold">Lifetime value</th>
              <th className="text-right px-4 py-2 font-bold">Last order</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && data.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-3 text-sm">Loading…</td></tr>
            )}
            {!loading && data.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-3 text-sm">No customers yet.</td></tr>
            )}
            {data.map((c) => (
              <tr key={(c.id ?? 'guest') + ':' + c.email}
                onClick={() => navigate(`/admin/customers/${encodeURIComponent(c.id ?? c.email)}`)}
                className="hover:bg-surface transition-colors cursor-pointer">
                <td className="px-4 py-3 text-ink">{c.fullName ?? '—'}</td>
                <td className="px-4 py-3 text-ink-2">{c.email}</td>
                <td className="px-4 py-3">
                  <span className={'text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ' +
                    (c.role === 'admin' ? 'bg-gold/15 text-gold-dark' :
                     c.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                     c.role === 'production' ? 'bg-indigo-100 text-indigo-800' :
                     c.role === 'guest' ? 'bg-gray-100 text-gray-600' :
                     'bg-emerald-50 text-emerald-700')}>
                    {c.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-ink-2">{c.orderCount}</td>
                <td className="px-4 py-3 text-right font-semibold text-ink">${(c.ltvCents / 100).toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-xs text-ink-3">{c.lastOrderAt ? new Date(c.lastOrderAt).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-xs text-ink-3 text-right">{data.length} customer{data.length === 1 ? '' : 's'}</div>
    </div>
  )
}
